/**
 * ExtranetMyAccesses — Página "Meus acessos" do portal externo (Fase E4).
 *
 * Lista todos os espaços/coleções concedidos ao usuário externo autenticado,
 * indicando nível de permissão, quando foi concedido e se está expirado.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-09 v1.0.0 - Versão inicial (Fase E do plano KB).
 */
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { BookOpen, Folder, Loader2, ShieldCheck } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useExtranetMyAccesses } from '@/hooks/kb/useKbExtranet';

function fmt(dt: string | null): string {
  if (!dt) return '—';
  try {
    return format(new Date(dt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return '—';
  }
}

export default function ExtranetMyAccesses() {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const base = `/extranet/${tenantSlug}`;
  const { data: rows = [], isLoading } = useExtranetMyAccesses(tenantSlug);

  return (
    <div className="space-y-4">
      <Helmet>
        <title>Meus acessos</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Cabeçalho da página */}
      <header className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
        <div>
          <h1 className="text-xl font-semibold leading-tight">Meus acessos</h1>
          <p className="text-sm text-muted-foreground">
            Espaços e coleções liberados para você neste portal.
          </p>
        </div>
      </header>

      {/* Estado: carregando */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Estado: vazio */}
      {!isLoading && rows.length === 0 && (
        <Card className="p-10 text-center">
          <p className="text-sm text-muted-foreground">
            Nenhum acesso foi concedido ao seu usuário ainda. Se você acredita que isso é um
            engano, entre em contato com o administrador da organização.
          </p>
        </Card>
      )}

      {/* Lista de acessos */}
      {!isLoading && rows.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {rows.map((r) => {
            const isSpace = !!r.space_id;
            const title = isSpace ? r.space_name ?? '—' : r.collection_name ?? '—';
            // Link direto ao space quando aplicável; coleções não têm rota pública dedicada ainda.
            const href = isSpace && r.space_slug ? `${base}/espacos/${r.space_slug}` : null;

            const content = (
              <Card
                className={`p-4 h-full transition-colors ${
                  r.is_expired
                    ? 'opacity-60'
                    : href
                      ? 'hover:border-primary/40 hover:bg-muted/40'
                      : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 shrink-0 rounded-md bg-primary/10 flex items-center justify-center">
                    {isSpace ? (
                      <BookOpen className="h-5 w-5 text-primary" />
                    ) : (
                      <Folder className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium truncate">{title}</span>
                      <Badge variant={isSpace ? 'secondary' : 'outline'}>
                        {isSpace ? 'Espaço' : 'Coleção'}
                      </Badge>
                      <Badge variant={r.access_level === 'comment' ? 'default' : 'secondary'}>
                        {r.access_level === 'comment' ? 'Comentar' : 'Leitura'}
                      </Badge>
                      {r.is_expired && <Badge variant="destructive">Expirado</Badge>}
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                      <div>Concedido em {fmt(r.granted_at)}</div>
                      {r.expires_at && <div>Expira em {fmt(r.expires_at)}</div>}
                    </div>
                  </div>
                </div>
              </Card>
            );

            return href && !r.is_expired ? (
              <Link key={r.access_id} to={href} className="block focus:outline-none">
                {content}
              </Link>
            ) : (
              <div key={r.access_id}>{content}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}
